<template>
  <div>
    <div class="tab-body__header">
    </div>
    <div class="tab-body__grid">
      <ContactDialog
        v-if="modalVisible"
        @contactUpdated="modifyContact"
        @closeModal="closeModal"
        :currentContact="currentContact"
        :isByName="true"
      />
    </div>
    <div v-if="error" class="error">{{ error }}</div>
    <div v-else-if="!data" class="loading">Loading...</div>
    <div v-else-if="!data.success" class="error">{{ (data as ErrorResponse).message }}</div>
    <Grid
      v-else
      :style="{ height: 'auto' }"
      :data-items="data.response.ListItems"
      :columns="columns"
      resizable
      scrollable="true"
      sortable
      :sort="sort"
      :pageable="pageable"
      :page-size="pageSize"
      :skip="request.skip"
      :take="request.take"
      :total="data.response.TotalItemsInList"
      :filter="filter"
      :filterable="true"
      @sortchange="sortChangeHandler"
      @filterchange="filterChangeHandler"
      @pagechange="pageChangeHandler"
    >
      <template v-slot:EditRow="{ props: { dataItem } }">
        <td class="text-center k-table-td edit-icon">
          <font-awesome-icon icon="edit" @click="showModal(true, dataItem)" />
        </td>
      </template>
      <template v-slot:emailCell="{ props: { dataItem, field } }">
        <td class="k-table-td">
          <a :href="dataItem[field]">{{
            dataItem[field] ?? dataItem.Contact?.Email ?? ""
          }}</a>
        </td>
      </template>
    </Grid>
  </div>
</template>

<script setup lang="ts">
import { onBeforeMount, onUpdated, ref, watch } from "vue";
import type {
  GridColumnProps,
  GridPagerSettings,
  GridPageChangeEvent,
  GridSortChangeEvent,
  GridFilterChangeEvent,
} from "@progress/kendo-vue-grid";
import type { CompositeFilterDescriptor } from "@progress/kendo-data-query";
import type {
  FilterDescriptor,
  SortDescriptor,
} from "@progress/kendo-data-query";
import { Grid } from "@progress/kendo-vue-grid";
import useSWRV from "swrv";
import { api, type ErrorResponse } from "@/api";
import type { GetCustomerContactsRequest } from "@/api/customerContacts";
import type { CustomerContact } from "@/Domain/CustomerContact";
import ContactDialog from "./ContactDialog.vue";
import { useMainStore } from "@/stores/useMainStore";
import type { Contact } from "@/Domain/Contact";
import { GridOptionsService } from "@/shared/services/GridOptionsService";

const main = useMainStore();
let companyLogin = main.companyLogin;

const currentContact = ref({} as CustomerContact);
const modalVisible = ref(false);

const columns = ref([
  {
    cell: "EditRow",
    sortable: false,
    filterable: false,
    width: "50px",
  },
  {
    title: "Job Title",
    field: "companyTitle",
  },
  {
    title: "Title",
    field: "Contact.Title",
  },
  {
    title: "First Name",
    field: "Contact.Firstname",
  },
  {
    title: "Last Name",
    field: "Contact.Lastname",
    width: "200px",
  },
  {
    title: "Email",
    field: "Contact.Email",
    cell: "emailCell",
    width: "200px",
  },
  {
    title: "Phone",
    field: "Contact.Voice",
  },
  {
    title: "Cell",
    field: "Contact.Cell",
  },
  {
    title: "Fax",
    field: "Contact.Fax",
  },
] as GridColumnProps[]);

const pageSize = ref(20);
const pageable = ref<GridPagerSettings>({
  buttonCount: 5,
  info: true,
  type: "numeric",
  pageSizes: [10, 20, 50, 100],
  previousNext: true,
});

const sort = ref([
  { field: "Contact.Lastname", dir: "asc" },
] as SortDescriptor[]); // Ideally we would include this filter object in the "request" object instead of including "fields" and "dirs", but our ancient WcfRestService doesn't support using objects in the Http request

const filter = ref<CompositeFilterDescriptor>({
  logic: "and",
  filters: [{} as FilterDescriptor], // { field: "Contact.Title", operator: "eq", value: '' },
});

const request = ref<GetCustomerContactsRequest>({
  userLogin: main.userLogin,
  companyLogin: companyLogin,
  sortFields: "Contact.Lastname",
  sortDirs: "asc",
  isCompanyContact: true, // doesn't matter since allContacts is true
  id: "",
  allContacts: true,
  distinct: true,
  fields: filter.value.filters
    .map((f) => (f as FilterDescriptor).field)
    .join(";"),
  operators: filter.value.filters
    .map((f) => (f as FilterDescriptor).operator)
    .join(";"),
  values: filter.value.filters
    .map((f) => (f as FilterDescriptor).value)
    .join(";"),
  skip: 0,
  take: pageSize.value,
});

const { error, data, mutate } = useSWRV(
  () => {
    let ret = ""; // build the key based on all the name.value pairs of the request object
    Object.keys(request.value).forEach(
      (x) => (ret += ":" + x + "=" + Object(request.value)[x])
    );
    return ret;
  },
  () => {
    {
      return api.get("GetCustomerContacts", request.value);
    }
  }
);

const gridName = "ByNameContactGrid";

function showModal(isVisible: boolean, cc: CustomerContact | undefined) {
  modalVisible.value = isVisible;
  if (cc) currentContact.value = cc;
}

function pageChangeHandler(event: GridPageChangeEvent) {
  request.value.skip = event.page.skip;
  if (request.value.take != event.page.take) {
    request.value.take = event.page.take;
    GridOptionsService.savePageSizeAsync(gridName, request.value.take);
  }
}

function sortChangeHandler(e: GridSortChangeEvent) {
  console.debug(
    "ByName.vue: sortChangeHandle(): r e = " + JSON.stringify(e.sort)
  );
  sort.value = e.sort;
  request.value.sortFields = e.sort.map((f) => f.field).join(";");
  request.value.sortDirs = e.sort.map((f) => f.dir).join(";");
}

function resetFilter() {
  request.value.fields = filter.value.filters
    .map((f) => (f as FilterDescriptor).field)
    .join(";");
  request.value.operators = filter.value.filters
    .map((f) => (f as FilterDescriptor).operator)
    .join(";");
  request.value.values = filter.value.filters
    .map((f) => (f as FilterDescriptor).value)
    .join(";");
  request.value.skip = 0;
  request.value.take = pageSize.value;
}

function clearFilters() {
  filter.value.filters.splice(0, filter.value.filters.length);
  resetFilter();
}

function filterChangeHandler(e: GridFilterChangeEvent) {
  filter.value.filters = [
    {
      field: e.event.field,
      operator: e.event.operator,
      value: e.event.value,
    } as FilterDescriptor,
  ];
  resetFilter();
}

function closeModal() {
  modalVisible.value = false;
}

function modifyContact(cc: Contact | undefined) {
  showModal(false, currentContact.value);

  if (cc) {
    currentContact.value.Contact = cc;
    mutate();
  }
}

// "main" can be changed in one of two ways.
// 1. A new company is being emulated.
// 2. The company hasn't changed, but a contact has been added or modified.
watch(main, () => {
  console.debug(
    "ByName.vue: watch(): main changed. companyLogin = " +
      companyLogin +
      ", main.companyLogin = " +
      main.companyLogin
  );
  if (main.companyLogin != companyLogin) {
    companyLogin = main.companyLogin;
    request.value.companyLogin = companyLogin;
    mutate();
  }
});

onBeforeMount(async () => {
  const pageSize = await GridOptionsService.getPageSizeAsync(gridName);
  if (pageSize) request.value.take = pageSize;
});
</script>

<style scoped>
.clear-filters {
  margin-left: auto;
  display: flex;
  align-items: center;
}
.clear-filters input {
  border: 1px solid #ddd;
  border-radius: 4px;
  padding-left: 25px;
}
.del-icon {
  font-size: 16px;
  color: red;
  cursor: pointer;
  display: block;
}
.edit-icon {
  font-size: 18px;
  color: #222;
  cursor: pointer;
  display: block;
}
.tab-body__grid-title {
  background: white;
  padding: 15px;
  border-left: 1px solid #ddd;
  border-right: 1px solid #ddd;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #ccc;
}
.loading {
  padding: 20px 10px;
  background: white;
  border: 1px solid #ddd;
}
.fa-edit {
  color: #999;
}
.last-name-filter {
  max-width: 75%;
  height: 38px;
  margin-right: 5px;
}
:deep(.k-column-title) {
  white-space: pre-wrap;
}
</style>
